//@flow

export const DEFAULT_EXECUTION_MINUTE = 0;

export const STANDARD_HOUR_OPTIONS = [
  {
    label: 'Horario 1',
    value: '08:00-05:00',
    utcValue: '13:00+00:00',
  },
  {
    label: 'Horario 2',
    value: '10:00-05:00',
    utcValue: '15:00+00:00',
  },
  {
    label: 'Horario 3',
    value: '12:00-05:00',
    utcValue: '17:00+00:00',
  },
  {
    label: 'Horario 4',
    value: '15:00-05:00',
    utcValue: '20:00+00:00',
  },
  {
    label: 'Horario 5',
    value: '18:00-05:00',
    utcValue: '23:00+00:00',
  },
  {
    label: 'Horario 6',
    value: '20:00-05:00',
    utcValue: '01:00+00:00',
  },
];

export const DEFAULT_EXECUTION_HOUR_SLOT = STANDARD_HOUR_OPTIONS[0].value;

export function parseHourSlotToExecutionTime(
  slotValue: string
): {
  hour: number,
  minute: number,
} {
  const hourPart = slotValue.split('-')[0];
  const [hour, minute] = hourPart.split(':').map(Number);

  return {
    hour,
    minute: Number.isFinite(minute) ? minute : DEFAULT_EXECUTION_MINUTE,
  };
}

export function executionTimeToHourSlot(executionTime: {
  hour: number,
  minute?: number,
}): string {
  const hour = String(executionTime.hour).padStart(2, '0');
  const minute = String(
    executionTime.minute ?? DEFAULT_EXECUTION_MINUTE
  ).padStart(2, '0');

  return `${hour}:${minute}-05:00`;
}

/** Convert a local hour-slot value (e.g. `08:00-05:00`) to UTC `HH:MM`. */
export function hourSlotToUtcHhMm(slotValue: string): string {
  const option = STANDARD_HOUR_OPTIONS.find(item => item.value === slotValue);
  if (option?.utcValue) {
    return option.utcValue.slice(0, 5);
  }

  const local = parseHourSlotToExecutionTime(slotValue);
  const localDate = new Date();
  localDate.setHours(local.hour, local.minute, 0, 0);

  return `${String(localDate.getUTCHours()).padStart(2, '0')}:${String(
    localDate.getUTCMinutes()
  ).padStart(2, '0')}`;
}

/** Map a UTC `HH:MM` string back to a local hour-slot option value. */
export function utcHhMmToHourSlot(utcHhMm: ?string): string {
  if (!utcHhMm || typeof utcHhMm !== 'string') {
    return DEFAULT_EXECUTION_HOUR_SLOT;
  }

  const normalized = utcHhMm.slice(0, 5);
  const byUtc = STANDARD_HOUR_OPTIONS.find(
    item => item.utcValue.slice(0, 5) === normalized
  );
  if (byUtc) return byUtc.value;

  // Legacy nested {hour, minute} was stored as local time — try matching local slot.
  const byLocal = STANDARD_HOUR_OPTIONS.find(item =>
    item.value.startsWith(normalized)
  );
  if (byLocal) return byLocal.value;

  return DEFAULT_EXECUTION_HOUR_SLOT;
}

/**
 * Resolve a form/API execution time value into a local hour-slot.
 * Accepts UTC `HH:MM`, a slot value, or legacy `{hour, minute}` (local).
 */
export function resolveExecutionTimeSlot(
  executionTime: ?(string | {hour?: number, minute?: number})
): string {
  if (!executionTime) return DEFAULT_EXECUTION_HOUR_SLOT;

  if (typeof executionTime === 'string') {
    if (executionTime.includes('-')) {
      return (
        STANDARD_HOUR_OPTIONS.find(item => item.value === executionTime)
          ?.value || DEFAULT_EXECUTION_HOUR_SLOT
      );
    }
    return utcHhMmToHourSlot(executionTime);
  }

  if (typeof executionTime.hour === 'number') {
    return executionTimeToHourSlot({
      hour: executionTime.hour,
      minute: executionTime.minute ?? DEFAULT_EXECUTION_MINUTE,
    });
  }

  return DEFAULT_EXECUTION_HOUR_SLOT;
}

export function getStandardHourOptionLabel(slotValue: string): string {
  const option = STANDARD_HOUR_OPTIONS.find(item => item.value === slotValue);
  return option?.label || slotValue;
}

export function getHourOptionLabelFromUtc(utcHhMm: ?string): string {
  return getStandardHourOptionLabel(utcHhMmToHourSlot(utcHhMm));
}

// Kept for schedule messages (subset of standard slots used there).
export const SCHEDULE_HOUR_PRESET_OPTIONS = STANDARD_HOUR_OPTIONS.filter(
  option =>
    option.value === '10:00-05:00' ||
    option.value === '15:00-05:00' ||
    option.value === '20:00-05:00'
);
